"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TimeTrackingLogFieldLogic = void 0;
const differenceBy_1 = __importDefault(require("lodash-es/differenceBy"));
const differenceWith_1 = __importDefault(require("lodash-es/differenceWith"));
const mean_1 = __importDefault(require("lodash-es/mean"));
const number_1 = require("../../aggregations/number");
const clipboard_1 = require("../../clipboard/clipboard");
const comparisons_1 = require("../../comparisons/comparisons");
const types_1 = require("@smartsuite/types");
const field_logic_1 = require("../../field-type-registry/field-logic");
const intl_1 = require("../../locale/intl");
const get_value_1 = require("../../safe-field-value/date-fields/time-tracking-field/get-value");
const time_tracking_fieldtype_1 = require("../../safe-field-value/date-fields/time-tracking-field/time-tracking.fieldtype");
const time_tracking_1 = require("../../safe-field-value/type-checking/time-tracking");
const texts_1 = require("../../texts");
const duration_1 = require("../../utils/duration");
const migration_1 = require("../../utils/migration");
const validation_1 = require("../../validation/validation");
class TimeTrackingLogFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "timetrackingfield";
        this.label = (0, intl_1.$t)(texts_1.timeTrackingLogFieldText.time_tracking_log);
        this.icon = "time-tracking";
        this.iconColor = "#FFB938";
        this.searchSynonyms = [(0, intl_1.$t)(texts_1.timeTrackingLogFieldText.spent)];
        this.placeholder = (0, intl_1.$t)(texts_1.timeTrackingLogFieldText.type_minutes_or_hours);
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is_equal_to: comparisons_1.isEqualTo,
            is_not_equal_to: comparisons_1.isNotEqualTo,
            is_greater_than: comparisons_1.isGreaterThan,
            is_less_than: comparisons_1.isLessThanAndNotEmpty,
            is_equal_or_greater_than: comparisons_1.isEqualOrGreaterThan,
            is_equal_or_less_than: comparisons_1.isEqualOrLessThanAndNotEmpty,
            is_empty: comparisons_1.isObjectFieldEmpty,
            is_not_empty: comparisons_1.isNotObjectFieldEmpty,
        };
        this.fieldValueGetter = new time_tracking_fieldtype_1.TimeTrackingFieldValueGetter();
        this.aggregates = {
            empty: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.empty),
                aggregator: field_logic_1.countEmpty,
            },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            sum: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.sum),
                aggregator: (0, number_1.numbersAggregate)((values) => values.reduce((acc, value) => acc + value, 0)),
            },
            lowest: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.lowest),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? Math.min(...values) : null),
            },
            highest: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.highest),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? Math.max(...values) : null),
            },
            average: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.average),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? (0, mean_1.default)(values) : null),
            },
            range: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.range),
                aggregator: number_1.numberRangeAggregator,
            },
        };
        this.clipboardPasteRules = {
            timetrackingfield: clipboard_1.clipboardTimeTrackingToSelf,
        };
    }
    get validators() {
        return { required: validation_1.requiredTimeTrackingField };
    }
    getEmptyValue() {
        return (0, get_value_1.getTimeTrackingFieldEmptyValue)();
    }
    processFieldValue(record, slug, comparison, field) {
        var _a;
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        // Field value needs to be rounded according to the display format before the comparison to match what's being shown in the report
        const displayValue = (0, duration_1.formatDisplayDuration)((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.total_duration) !== null && _a !== void 0 ? _a : 0, "compact", "hh:mm");
        return (0, duration_1.convertInputDuration)(displayValue);
    }
    valueToString(fieldValue, _field, ..._rest) {
        if (typeof fieldValue === "number") {
            return (0, duration_1.formatDisplayDuration)(fieldValue, "compact", "hh:mm");
        }
        if (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.total_duration) {
            return (0, duration_1.formatDisplayDuration)(fieldValue.total_duration, "compact", "hh:mm");
        }
        return "";
    }
    filterValueToString(filterValue, _field, ..._rest) {
        return (0, duration_1.formatDisplayDuration)(filterValue, "compact", "hh:mm");
    }
    sortKey(fieldValue, _field) {
        return Number(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.total_duration) || 0;
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            closeWhenClickOutside: false,
            editorWidth: "100%",
            editOnStartTyping: false,
            saveKeys: [],
            allowAddRecordToGroup: false,
        };
    }
    isDefaultEmpty(_field) {
        return true;
    }
    isValueEmpty(fieldValue, field) {
        var _a;
        return (super.isValueEmpty(fieldValue, field) ||
            (!fieldValue.total_duration && !((_a = fieldValue.time_track_logs) === null || _a === void 0 ? void 0 : _a.length)));
    }
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, field) {
        var _a, _b;
        if (this.isValueEmpty(previousValue, field) ||
            this.isValueEmpty(currentValue, field)) {
            return { previousValue, currentValue };
        }
        const totalDurationChanged = previousValue.total_duration !== currentValue.total_duration;
        const formattedPreviousValue = {
            total_duration: totalDurationChanged
                ? previousValue.total_duration
                : null,
            time_track_logs: (0, differenceBy_1.default)(previousValue.time_track_logs, (_a = currentValue.time_track_logs) !== null && _a !== void 0 ? _a : [], "note"),
        };
        const formattedCurrentValue = {
            total_duration: totalDurationChanged ? currentValue.total_duration : null,
            time_track_logs: (0, differenceBy_1.default)(currentValue.time_track_logs, (_b = previousValue.time_track_logs) !== null && _b !== void 0 ? _b : [], "note"),
        };
        return {
            previousValue: formattedPreviousValue,
            currentValue: formattedCurrentValue,
        };
    }
    isValueEqual(fieldValue1, fieldValue2, _field, _userPreferences) {
        var _a, _b, _c, _d;
        if (!fieldValue1 && !fieldValue2)
            return true;
        if (!fieldValue1 ||
            !fieldValue2 ||
            fieldValue1.total_duration !== fieldValue2.total_duration ||
            ((_a = fieldValue1.time_track_logs) === null || _a === void 0 ? void 0 : _a.length) !==
                ((_b = fieldValue2.time_track_logs) === null || _b === void 0 ? void 0 : _b.length)) {
            return false;
        }
        const difference = (0, differenceWith_1.default)((_c = fieldValue1.time_track_logs) !== null && _c !== void 0 ? _c : [], (_d = fieldValue2.time_track_logs) !== null && _d !== void 0 ? _d : [], (log1, log2) => {
            return (log1.duration === log2.duration &&
                log1.note === log2.note &&
                log1.user_id === log2.user_id &&
                log1.time_range === log2.time_range &&
                log1.date_time === log2.date_time);
        });
        return !(difference === null || difference === void 0 ? void 0 : difference.length);
    }
    canChangeValue(field, fromValue, toValue) {
        return this.isValueEqual(fromValue, toValue, field);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 110,
            minWidth: 60,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.timeTrackDisplayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
    metricWidgetValueToString(fieldValue, field, userPreferences) {
        const hours = Number((Number(fieldValue) / 3600).toFixed(1));
        const formatted = super.metricWidgetValueToString(hours, field, userPreferences);
        return `${formatted} hours`;
    }
    getAggregateDisplayValue(fieldValue) {
        if (!fieldValue) {
            return "";
        }
        return (0, duration_1.formatDisplayDuration)(Number(fieldValue), "compact", "hh:mm", true);
    }
    isSupportedByBulkAction() {
        return false;
    }
    isFullValueLoadNeeded(value) {
        return !(0, time_tracking_1.isFullTimeTrackingValue)(value);
    }
    isPartialValue(value) {
        return !(0, time_tracking_1.isFullTimeTrackingValue)(value);
    }
}
exports.TimeTrackingLogFieldLogic = TimeTrackingLogFieldLogic;
